import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Product } from '@/data/products';
import { useCallback } from 'react';

// Centralized cache configuration
const CACHE_CONFIG = {
  products: {
    staleTime: 30 * 1000, // 30 seconds - reflect admin changes quickly
    gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
  },
  reviews: {
    staleTime: 2 * 60 * 1000, // 2 minutes - reviews update more often
    gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
  },
};

// Transform function to avoid code duplication
const transformProduct = (data: any): Product => ({
  id: data.id,
  name: data.name,
  tagline: data.tagline || '',
  price: Number(data.price),
  subscription_price: Number(data.subscription_price),
  description: data.description || '',
  benefits: Array.isArray(data.benefits) ? data.benefits.map(String) : [],
  ingredients: Array.isArray(data.ingredients) ? data.ingredients.map(String) : [],
  flavors: Array.isArray(data.flavors) ? data.flavors.map(String) : [],
  image: data.image_url || (Array.isArray(data.image_urls) && data.image_urls[0]) || '',
  images: Array.isArray(data.image_urls) && data.image_urls.length > 0
    ? data.image_urls.filter(Boolean)
    : (data.image_url ? [data.image_url] : []),
  category: data.category,
  stockQuantity: data.stock_quantity ?? 100,
  lowStockThreshold: data.low_stock_threshold ?? 10,
  packSizes: Array.isArray(data.pack_sizes) && data.pack_sizes.length > 0
    ? data.pack_sizes.map((ps: any) => ({
        size: Number(ps.size) || 12,
        price: Number(ps.price) || 0,
        subscription_price: Number(ps.subscription_price) || (Number(ps.price) * 0.85)
      }))
    : [],
  isPreorder: !!data.is_preorder,
  preorderReleaseDate: data.preorder_release_date ?? null,
  preorderMessage: data.preorder_message ?? null,
});

export const useProducts = () => {
  return useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, tagline, price, subscription_price, description, benefits, ingredients, flavors, image_url, image_urls, category, pack_sizes, stock_quantity, low_stock_threshold, is_preorder, preorder_release_date, preorder_message')
        .eq('active', true)
        .order('name');

      if (error) throw error;
      return data.map(transformProduct);
    },
    staleTime: CACHE_CONFIG.products.staleTime,
    gcTime: CACHE_CONFIG.products.gcTime,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
};

export const useProduct = (productId: string) => {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: ['product', productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, tagline, price, subscription_price, description, benefits, ingredients, flavors, image_url, image_urls, category, pack_sizes, stock_quantity, low_stock_threshold, is_preorder, preorder_release_date, preorder_message')
        .eq('id', productId)
        .single();

      if (error) throw error;
      return transformProduct(data);
    },
    enabled: !!productId,
    staleTime: CACHE_CONFIG.products.staleTime,
    gcTime: CACHE_CONFIG.products.gcTime,
    // Use products list cache if available
    placeholderData: () => {
      const products = queryClient.getQueryData<Product[]>(['products']);
      return products?.find(p => p.id === productId);
    },
  });
};

export const useProductReviews = (productId: string) => {
  return useQuery({
    queryKey: ['product-reviews', productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_reviews')
        .select('id, rating, review_text, title, created_at, user_id, verified_purchase, helpful_votes, status')
        .eq('product_id', productId)
        .eq('status', 'approved') // Only show approved reviews
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      return data;
    },
    enabled: !!productId,
    staleTime: CACHE_CONFIG.reviews.staleTime,
    gcTime: CACHE_CONFIG.reviews.gcTime,
  });
};

// Prefetch hook for anticipatory loading
export const usePrefetchProduct = () => {
  const queryClient = useQueryClient();
  
  return useCallback((productId: string) => {
    queryClient.prefetchQuery({
      queryKey: ['product', productId],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('products')
          .select('id, name, tagline, price, subscription_price, description, benefits, ingredients, flavors, image_url, image_urls, category, pack_sizes, stock_quantity, low_stock_threshold, is_preorder, preorder_release_date, preorder_message')
          .eq('id', productId)
          .single();
        if (error) throw error;
        return transformProduct(data);
      },
      staleTime: CACHE_CONFIG.products.staleTime,
    });
  }, [queryClient]);
};
